import React, { useState, useEffect, useMemo } from 'react';
import { 
  FileCheck2, 
  Clock, 
  HelpCircle, 
  Play, 
  Lock, 
  Sparkles, 
  Search, 
  Filter, 
  ShieldCheck, 
  PenTool, 
  RotateCcw, 
  Award, 
  ChevronRight, 
  BookOpen, 
  CheckCircle2, 
  AlertCircle,
  Layers,
  ArrowRight
} from 'lucide-react';
import { MockExam, UserProfile, PraxisPaper, MockAttempt } from '../types';
import { getAllPublishedMockExams, getUserMockAttempts } from '../lib/mockService';
import { isPaperUnlockedForUser, isUserAdmin, getPaperCatalogSummaries } from '../lib/firebase';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import { examCatalog } from '../data/examCatalog';

export interface MockExamsHubProps {
  userProfile: UserProfile;
  onStartMockExam: (mockExam: MockExam) => void;
  openPricingModal: () => void;
  initialFamilyFilter?: string | null;
  onSelectExamDirectory?: (familyOrState: string) => void;
  onOpenGuides?: (paperId: string) => void;
}

export interface ExamFamilyMeta {
  id: string;
  name: string;
  shortLabel: string;
  badge: string;
  states: string;
  color: string;
}

export const EXAM_FAMILIES: ExamFamilyMeta[] = [
  { id: 'all', name: 'All Exam Families', shortLabel: 'All', badge: 'Complete Catalog', states: 'All States & National', color: 'indigo' },
  { id: 'praxis', name: 'Praxis Series', shortLabel: 'Praxis', badge: 'ETS National', states: '40+ States & Territories', color: 'indigo' },
  { id: 'cbest', name: 'CBEST', shortLabel: 'CBEST', badge: 'CTC California', states: 'California & Oregon', color: 'blue' },
  { id: 'cset', name: 'CSET', shortLabel: 'CSET', badge: 'CTC California', states: 'California', color: 'sky' },
  { id: 'nystce', name: 'NYSTCE', shortLabel: 'NYSTCE', badge: 'NYSED New York', states: 'New York', color: 'amber' },
  { id: 'texes', name: 'TExES', shortLabel: 'TExES', badge: 'TEA / SBEC', states: 'Texas', color: 'emerald' },
  { id: 'ftce', name: 'FTCE', shortLabel: 'FTCE', badge: 'FLDOE Florida', states: 'Florida', color: 'teal' },
  { id: 'ilts', name: 'ILTS', shortLabel: 'ILTS', badge: 'ISBE Illinois', states: 'Illinois', color: 'purple' },
  { id: 'pect_papa', name: 'PECT / PAPA', shortLabel: 'PECT / PAPA', badge: 'PDE Pennsylvania', states: 'Pennsylvania', color: 'cyan' },
  { id: 'gace', name: 'GACE', shortLabel: 'GACE', badge: 'GaPSC Georgia', states: 'Georgia', color: 'violet' }
];

// Helper to determine exam family for any paper / mock exam
export function resolveExamFamily(paperId: string, paper?: PraxisPaper): string {
  const pId = paperId.toLowerCase();
  const examCode = (paper?.examCode || '').toLowerCase();
  const code = (paper?.code || '').toLowerCase();
  const category = (paper?.category || '').toLowerCase();

  if (pId.startsWith('cbest') || examCode.includes('cbest') || code.startsWith('cbest')) return 'cbest';
  if (pId.startsWith('cset') || examCode.includes('cset') || code.startsWith('cset')) return 'cset';
  if (pId.startsWith('nystce') || examCode.includes('nystce') || code.startsWith('nystce')) return 'nystce';
  if (pId.startsWith('texes') || examCode.includes('texes') || code.startsWith('texes')) return 'texes';
  if (pId.startsWith('ftce') || examCode.includes('ftce') || code.startsWith('ftce')) return 'ftce';
  if (pId.startsWith('ilts') || examCode.includes('ilts') || code.startsWith('ilts')) return 'ilts';
  if (pId.startsWith('pect') || pId.startsWith('papa') || examCode.includes('pect') || examCode.includes('papa') || code.startsWith('pect') || code.startsWith('papa')) return 'pect_papa';
  if (pId.startsWith('gace') || examCode.includes('gace') || code.startsWith('gace')) return 'gace';
  if (examCode.includes('praxis') || category === 'core' || category === 'elementary' || category === 'plt' || category === 'subject' || !isNaN(Number(paperId))) {
    return 'praxis';
  }
  return 'praxis';
}

export function getExamFamilyLabel(familyId: string): string {
  const matched = EXAM_FAMILIES.find(f => f.id === familyId);
  return matched ? matched.name : 'Praxis Series';
}

export const MockExamsHub: React.FC<MockExamsHubProps> = ({
  userProfile,
  onStartMockExam,
  openPricingModal,
  initialFamilyFilter = 'all',
  onSelectExamDirectory,
  onOpenGuides
}) => {
  const [mockExams, setMockExams] = useState<MockExam[]>([]);
  const [papers, setPapers] = useState<PraxisPaper[]>(PRAXIS_PAPERS);
  const [userAttempts, setUserAttempts] = useState<MockAttempt[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedFamily, setSelectedFamily] = useState<string>(() => {
    if (!initialFamilyFilter || initialFamilyFilter === 'all') return 'all';
    const norm = initialFamilyFilter.toLowerCase();
    if (norm.includes('california') || norm.includes('cset')) return 'cset';
    if (norm.includes('cbest')) return 'cbest';
    if (norm.includes('new york') || norm.includes('nystce')) return 'nystce';
    if (norm.includes('texas') || norm.includes('texes')) return 'texes';
    if (norm.includes('florida') || norm.includes('ftce')) return 'ftce';
    if (norm.includes('illinois') || norm.includes('ilts')) return 'ilts';
    if (norm.includes('pennsylvania') || norm.includes('pect') || norm.includes('papa')) return 'pect_papa';
    if (norm.includes('georgia') || norm.includes('gace')) return 'gace';
    if (norm.includes('praxis')) return 'praxis';
    return 'all';
  });
  const [searchQuery, setSearchQuery] = useState<string>('');

  const isUnlocked = isPaperUnlockedForUser(userProfile) || isUserAdmin(userProfile);

  // Sync external initialFamilyFilter if changed
  useEffect(() => {
    if (initialFamilyFilter) {
      const norm = initialFamilyFilter.toLowerCase();
      if (norm === 'all') setSelectedFamily('all');
      else if (norm.includes('california') || norm.includes('cset')) setSelectedFamily('cset');
      else if (norm.includes('cbest')) setSelectedFamily('cbest');
      else if (norm.includes('new york') || norm.includes('nystce')) setSelectedFamily('nystce');
      else if (norm.includes('texas') || norm.includes('texes')) setSelectedFamily('texes');
      else if (norm.includes('florida') || norm.includes('ftce')) setSelectedFamily('ftce');
      else if (norm.includes('illinois') || norm.includes('ilts')) setSelectedFamily('ilts');
      else if (norm.includes('pennsylvania') || norm.includes('pect') || norm.includes('papa')) setSelectedFamily('pect_papa');
      else if (norm.includes('georgia') || norm.includes('gace')) setSelectedFamily('gace');
      else if (norm.includes('praxis')) setSelectedFamily('praxis');
    }
  }, [initialFamilyFilter]);

  // Load all published mock exams, papers, and user past mock attempts
  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      setLoading(true);
      try {
        const [loadedMocks, firestorePapers, attempts] = await Promise.all([
          getAllPublishedMockExams(),
          getPaperCatalogSummaries().catch(() => PRAXIS_PAPERS),
          (userProfile?.uid && userProfile.uid !== 'guest_user_1')
            ? getUserMockAttempts(userProfile.uid).catch(() => [])
            : Promise.resolve([])
        ]);

        if (isMounted) {
          setMockExams(loadedMocks);
          if (Array.isArray(firestorePapers) && firestorePapers.length > 0) {
            setPapers(firestorePapers);
          }
          if (Array.isArray(attempts)) {
            setUserAttempts(attempts);
          }
        }
      } catch (err) {
        console.warn('MockExamsHub: Error loading mock exams catalog:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    loadData();
    return () => {
      isMounted = false;
    };
  }, [userProfile?.uid]);

  const paperMap = useMemo(() => {
    const map = new Map<string, PraxisPaper>();
    papers.forEach(p => {
      map.set(p.id, p);
      map.set(p.code, p);
    });
    return map;
  }, [papers]);

  // Filter mock exams
  const filteredMocks = useMemo(() => {
    return mockExams.filter(mock => {
      const paper = paperMap.get(mock.paperId);
      const family = resolveExamFamily(mock.paperId, paper);
      
      const matchesFamily = selectedFamily === 'all' || family === selectedFamily;
      
      if (!matchesFamily) return false;

      if (!searchQuery.trim()) return true;

      const q = searchQuery.toLowerCase().trim();
      const paperTitle = (paper?.title || '').toLowerCase();
      const paperCode = (paper?.code || mock.paperId).toLowerCase();
      const series = (mock.seriesLabel || '').toLowerCase();
      const mockId = (mock.id || '').toLowerCase();
      const familyName = getExamFamilyLabel(family).toLowerCase();

      return (
        paperTitle.includes(q) ||
        paperCode.includes(q) ||
        series.includes(q) ||
        mockId.includes(q) ||
        familyName.includes(q)
      );
    });
  }, [mockExams, paperMap, selectedFamily, searchQuery]);

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8 animate-fadeIn">
      {/* Top Header Banner */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-xs relative overflow-hidden space-y-6">
        <div className="absolute top-0 right-0 transform translate-x-12 -translate-y-12 w-64 h-64 bg-indigo-50/70 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-slate-100">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-200 flex items-center gap-1.5 shadow-2xs">
                <FileCheck2 className="w-3.5 h-3.5 text-indigo-600" /> Standardized Mock Exams
              </span>
              <span className="text-xs font-bold text-slate-500">
                {mockExams.length} Full Standardized Set{mockExams.length === 1 ? '' : 's'} Active
              </span>
            </div>

            <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Mock Exams
            </h1>
            <p className="text-slate-600 text-sm leading-relaxed">
              Complete locked, full-length standardized mock exams matching official state and national educator licensing specifications. Receive instant domain accuracy breakdowns and unofficial scaled score estimates.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs space-y-2 shrink-0 max-w-sm">
            <div className="flex items-center gap-2 font-bold text-slate-800">
              <ShieldCheck className="w-4 h-4 text-indigo-600" /> Standardized Invariants
            </div>
            <ul className="text-slate-600 text-[11px] space-y-1 list-disc list-inside leading-relaxed">
              <li>Fixed, non-randomized test sets</li>
              <li>Strict timed test simulation environment</li>
              <li>Unofficial score reports & competency diagnostics</li>
            </ul>
          </div>
        </div>

        {/* Filter Bar & Family Tabs */}
        <div className="space-y-4 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
              <Filter className="w-4 h-4 text-indigo-600" />
              <span>Filter by Exam Family:</span>
            </div>

            <div className="relative min-w-[260px] sm:w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search mock exams (e.g. 5733, Math)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
              />
            </div>
          </div>

          {/* Horizontal Exam Family Filter Pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {EXAM_FAMILIES.map(family => {
              const isActive = selectedFamily === family.id;
              const familyCount = family.id === 'all'
                ? mockExams.length
                : mockExams.filter(m => resolveExamFamily(m.paperId, paperMap.get(m.paperId)) === family.id).length;

              return (
                <button
                  key={family.id}
                  onClick={() => setSelectedFamily(family.id)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-2 cursor-pointer ${
                    isActive
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                >
                  <span>{family.shortLabel}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${
                    isActive ? 'bg-indigo-700 text-white' : 'bg-slate-200 text-slate-700'
                  }`}>
                    {familyCount}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Grid: Published Mock Exam Cards */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <span>Available Standardized Mock Exam Series</span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
              {filteredMocks.length} Test{filteredMocks.length === 1 ? '' : 's'} Shown
            </span>
          </h2>

          {selectedFamily !== 'all' && (
            <button
              onClick={() => setSelectedFamily('all')}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition cursor-pointer"
            >
              Clear Family Filter
            </button>
          )}
        </div>

        {loading ? (
          <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 animate-pulse space-y-3">
            <FileCheck2 className="w-8 h-8 text-indigo-400 mx-auto animate-bounce" />
            <p className="text-xs font-semibold text-slate-500">
              Loading standardized mock exams from Firestore...
            </p>
          </div>
        ) : filteredMocks.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {filteredMocks.map((mock) => {
              const paper = paperMap.get(mock.paperId);
              const familyKey = resolveExamFamily(mock.paperId, paper);
              const familyLabel = getExamFamilyLabel(familyKey);
              const totalMc = mock.questionIds?.length || 0;
              const totalCr = mock.constructedResponsePromptIds?.length || 0;
              const duration = mock.durationMinutes || paper?.durationMinutes || 120;
              const passingScore = mock.passingScoreSnapshot ?? paper?.passingScoreEts ?? 150;
              const title = paper?.title || `Paper ${mock.paperId}`;
              const code = paper?.code || mock.paperId;

              // Check if user has attempted this mock
              const attemptsForThisMock = userAttempts.filter(a => a.mockExamId === mock.id);
              const completedAttempts = attemptsForThisMock.filter(a => a.status === 'completed');

              return (
                <div
                  key={mock.id}
                  className="bg-white rounded-2xl border-2 border-indigo-100 hover:border-indigo-300 shadow-xs hover:shadow-md transition-all p-6 flex flex-col justify-between space-y-5 relative overflow-hidden"
                >
                  <div className="space-y-4">
                    {/* Top Row: Badges & Identifiers */}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 rounded-lg text-xs font-black bg-indigo-600 text-white shadow-2xs">
                          {familyLabel}
                        </span>
                        <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                          {mock.seriesLabel || 'Standardized Mock'}
                        </span>
                      </div>

                      <span className="text-[11px] font-mono font-semibold text-slate-400">
                        {mock.id}
                      </span>
                    </div>

                    {/* Title & Subtest Info */}
                    <div>
                      <h3 className="font-extrabold text-slate-900 text-base leading-snug">
                        {code} — {title}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed line-clamp-2">
                        Official {familyLabel} standardized test specification. {totalMc} multiple choice questions
                        {totalCr > 0 ? ` and ${totalCr} constructed response prompts` : ''}.
                      </p>
                    </div>

                    {/* Metadata Badges */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-1">
                      <div className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-700 font-semibold">
                        <Clock className="w-4 h-4 text-indigo-600 shrink-0" />
                        <div>
                          <div className="text-[10px] text-slate-400 uppercase font-bold">Duration</div>
                          <div>{duration} Mins</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-700 font-semibold">
                        <HelpCircle className="w-4 h-4 text-indigo-600 shrink-0" />
                        <div>
                          <div className="text-[10px] text-slate-400 uppercase font-bold">Item Count</div>
                          <div>{totalMc} MC Qs</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-700 font-semibold col-span-2 sm:col-span-1">
                        <Award className="w-4 h-4 text-emerald-600 shrink-0" />
                        <div>
                          <div className="text-[10px] text-slate-400 uppercase font-bold">Passing Target</div>
                          <div>{passingScore} Pts</div>
                        </div>
                      </div>
                    </div>

                    {/* Domain Composition Snapshot Breakdown */}
                    {mock.domainCompositionSnapshot && mock.domainCompositionSnapshot.length > 0 && (
                      <div className="space-y-1.5 pt-1">
                        <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                          Blueprint Domain Distribution:
                        </div>
                        <div className="space-y-1 bg-slate-50/70 p-2.5 rounded-xl border border-slate-100">
                          {mock.domainCompositionSnapshot.map((dom, dIdx) => (
                            <div key={dIdx} className="flex items-center justify-between text-xs text-slate-700">
                              <span className="font-medium truncate mr-2">{dom.name}</span>
                              <span className="font-bold text-indigo-600 shrink-0">
                                {dom.mcCount} MC{dom.crCount > 0 ? ` + ${dom.crCount} CR` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Previous Attempts Status */}
                    {completedAttempts.length > 0 && (
                      <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-900 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 font-bold">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span>{completedAttempts.length} Completed Attempt{completedAttempts.length === 1 ? '' : 's'}</span>
                        </div>
                        <span className="text-[11px] text-emerald-700 font-medium">
                          Last: {new Date(completedAttempts[0].completedAt || '').toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions Footer */}
                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                    <span className="text-[11px] font-semibold text-emerald-700 flex items-center gap-1">
                      <ShieldCheck className="w-3.5 h-3.5" /> Locked Standard Set
                    </span>

                    {isUnlocked ? (
                      <button
                        onClick={() => onStartMockExam(mock)}
                        className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs transition shadow-xs flex items-center gap-2 cursor-pointer"
                      >
                        <Play className="w-3.5 h-3.5 fill-white" /> Start Mock Exam
                      </button>
                    ) : (
                      <button
                        onClick={openPricingModal}
                        className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs transition shadow-xs flex items-center gap-2 cursor-pointer"
                      >
                        <Lock className="w-3.5 h-3.5 text-amber-300" /> Unlock with Subscription
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Empty Filter State */
          <div className="p-10 text-center bg-white rounded-2xl border border-dashed border-slate-300 space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto">
              <FileCheck2 className="w-6 h-6" />
            </div>
            <div className="space-y-1 max-w-md mx-auto">
              <h3 className="text-base font-bold text-slate-800">
                No Mock Exams Found for "{selectedFamily === 'all' ? searchQuery : getExamFamilyLabel(selectedFamily)}"
              </h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Standardized mock exam series for this exam family are currently being authored according to state domain blueprints.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
              <button
                onClick={() => {
                  setSelectedFamily('all');
                  setSearchQuery('');
                }}
                className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition shadow-xs cursor-pointer"
              >
                View All Published Mock Exams
              </button>
              {onSelectExamDirectory && (
                <button
                  onClick={() => onSelectExamDirectory(selectedFamily)}
                  className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition cursor-pointer flex items-center gap-1.5"
                >
                  <BookOpen className="w-3.5 h-3.5 text-slate-500" /> Explore Exams Directory
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Information & State Integration Strip */}
      <div className="bg-slate-900 text-white rounded-2xl p-6 sm:p-8 shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-2 max-w-2xl">
          <div className="flex items-center gap-2 text-indigo-400 font-bold text-xs uppercase tracking-wider">
            <Layers className="w-4 h-4" /> Multi-Framework Standardization
          </div>
          <h3 className="text-lg sm:text-xl font-extrabold text-white">
            Looking for paper-level domain practice or study guides?
          </h3>
          <p className="text-xs text-slate-300 leading-relaxed">
            The Exams Directory hosts individual paper practice tests, adaptive drill modes, and domain competency study guides across all 8 major testing frameworks.
          </p>
        </div>

        <button
          onClick={() => {
            if (onSelectExamDirectory) {
              onSelectExamDirectory('all');
            }
          }}
          className="px-5 py-3 rounded-xl bg-white hover:bg-slate-100 text-slate-950 font-bold text-xs transition shadow-sm flex items-center gap-2 shrink-0 cursor-pointer"
        >
          <span>Exams Directory & Blueprints</span>
          <ArrowRight className="w-4 h-4 text-slate-900" />
        </button>
      </div>
    </div>
  );
};
